import { THealthDomain, TScreenerApiResponse } from '@/types/Screener.types';
import { HStack, VStack } from '@chakra-ui/react';
import React, { useMemo } from 'react';
import SingleDomain from './components/SingleDomain';
import CriticalQuestions from './components/CriticalQuestions';
import ReadinessDomain from './components/ReadinessDomain';
import { isUndefined } from 'lodash';

type Props = {
  screener: TScreenerApiResponse;
};

function chunkIntoColumns<T>(items: T[], columns: number): T[][] {
  const result: T[][] = Array.from({ length: columns }, () => []);
  items.forEach((item, index) => {
    result[index % columns].push(item);
  });
  return result;
}

const ScreenerResult = ({ screener }: Props) => {
  const { scores, subdomainScores, critical, readiness } = screener;

  const sortedObject = useMemo(() => {
    const sorted = Object.entries({ ...scores, readiness: readiness?.value }).sort(([, a], [, b]) => a - b);
    return Object.fromEntries(sorted);
  }, [scores, readiness]);

  const criticalItems = useMemo(() => critical?.questions.filter((q) => q.value === true) ?? [], [critical]);

  const isCritical = criticalItems.length > 0;
  const isV1Screener = isUndefined(subdomainScores);

  const flatItems = useMemo(() => {
    const items: React.ReactNode[] = [];

    if (isCritical) {
      items.push(<CriticalQuestions key="critical" questions={criticalItems} />);
    }

    Object.entries(sortedObject).forEach(([key, value]) => {
      if (key === 'readiness' && readiness) {
        items.push(<ReadinessDomain key="readiness" readiness={readiness} />);
        return;
      }

      if (isV1Screener) {
        items.push(<SingleDomain key={key} domain={key as THealthDomain} domainScore={value} />);
        return;
      }

      const subdomainScore = subdomainScores?.[key as THealthDomain];
      if (subdomainScore) {
        items.push(
          <SingleDomain key={key} domain={key as THealthDomain} domainScore={value} subdomainScore={subdomainScore} />,
        );
      }
    });

    if (!isCritical) {
      items.push(<CriticalQuestions key="critical-bottom" questions={criticalItems} />);
    }

    return items;
  }, [sortedObject, isCritical, criticalItems, isV1Screener, subdomainScores, readiness]);

  const columns = useMemo(() => chunkIntoColumns(flatItems, 3), [flatItems]);

  return (
    <HStack alignItems="flex-start" gap="20px" width="100%" alignSelf="stretch">
      {columns.map((columnItems, idx) => (
        <VStack key={idx} gap="20px" flex={1}>
          {columnItems}
        </VStack>
      ))}
    </HStack>
  );
};

export default ScreenerResult;
